import { useState } from "react";
import { useCondutores } from "@/features/transporte/hooks/useTransporte";
import type { Condutor, SalvarCondutorRequest } from "@/features/transporte/types";
import { CATEGORIAS_CNH } from "@/features/transporte/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, UserX } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const FORM_VAZIO: SalvarCondutorRequest = {
  nome: "",
  cpf: "",
  cnh: "",
  categoriaCnh: "",
  dtValidadeCnh: "",
  rg: "",
  telefone: "",
  ativo: true,
};

export default function CondutorPage() {
  const [busca, setBusca] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const { condutores, isLoading, criar, atualizar, excluir } = useCondutores(
    mostrarInativos ? undefined : { ativo: true }
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<Condutor | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [form, setForm] = useState<SalvarCondutorRequest>(FORM_VAZIO);

  const filtrados = busca
    ? condutores.filter(
        (c) =>
          c.nome.toLowerCase().includes(busca.toLowerCase()) ||
          c.cpf.includes(busca) ||
          (c.cnh ?? "").includes(busca)
      )
    : condutores;

  const paginacao = usePaginacaoLocal(filtrados);

  const abrirDialog = (condutor?: Condutor) => {
    if (condutor) {
      setEditando(condutor);
      setForm({
        nome: condutor.nome,
        cpf: condutor.cpf,
        cnh: condutor.cnh ?? "",
        categoriaCnh: condutor.categoriaCnh ?? "",
        dtValidadeCnh: condutor.dtValidadeCnh?.slice(0, 10) ?? "",
        rg: condutor.rg ?? "",
        telefone: condutor.telefone ?? "",
        ativo: condutor.ativo,
      });
    } else {
      setEditando(null);
      setForm(FORM_VAZIO);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editando) {
      await atualizar.mutateAsync({ id: editando.id, req: form });
    } else {
      await criar.mutateAsync(form);
    }
    setDialogOpen(false);
  };

  const formatarCpf = (cpf: string) =>
    cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");

  const validadeCnhVencida = (dt?: string) =>
    dt ? new Date(dt) < new Date() : false;

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Condutores</h1>
          <p className="text-muted-foreground">Motoristas para CT-e e MDFe</p>
        </div>
        <Button onClick={() => abrirDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Condutor
        </Button>
      </div>

      <div className="flex gap-4 items-center">
        <div className="max-w-sm flex-1">
          <Input
            placeholder="Buscar por nome, CPF ou CNH..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={mostrarInativos}
            onChange={(e) => setMostrarInativos(e.target.checked)}
          />
          Exibir inativos
        </label>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>CNH</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Validade CNH</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell>{formatarCpf(c.cpf)}</TableCell>
                <TableCell>{c.cnh ?? "-"}</TableCell>
                <TableCell>{c.categoriaCnh ?? "-"}</TableCell>
                <TableCell>
                  {c.dtValidadeCnh ? (
                    <span className={validadeCnhVencida(c.dtValidadeCnh) ? "text-destructive font-medium" : ""}>
                      {new Date(c.dtValidadeCnh).toLocaleDateString("pt-BR")}
                    </span>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{c.telefone ?? "-"}</TableCell>
                <TableCell>
                  <Badge variant={c.ativo ? "default" : "secondary"}>
                    {c.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => abrirDialog(c)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {c.ativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(c.id)}>
                        <UserX className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum condutor encontrado para esta busca." : "Nenhum condutor cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* ── Dialog Criar / Editar ─────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? "Editar Condutor" : "Novo Condutor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  required
                  maxLength={80}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">CPF *</Label>
                <Input
                  id="cpf"
                  value={form.cpf}
                  onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                  required
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rg">RG</Label>
                <Input
                  id="rg"
                  value={form.rg ?? ""}
                  onChange={(e) => setForm({ ...form, rg: e.target.value })}
                  maxLength={20}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cnh">CNH</Label>
                <Input
                  id="cnh"
                  value={form.cnh ?? ""}
                  onChange={(e) => setForm({ ...form, cnh: e.target.value })}
                  maxLength={20}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria CNH</Label>
                <Select
                  value={form.categoriaCnh ?? ""}
                  onValueChange={(v) => setForm({ ...form, categoriaCnh: v })}
                >
                  <SelectTrigger id="categoria">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS_CNH.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="validade">Validade CNH</Label>
                <Input
                  id="validade"
                  type="date"
                  value={form.dtValidadeCnh ?? ""}
                  onChange={(e) => setForm({ ...form, dtValidadeCnh: e.target.value })}
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={form.telefone ?? ""}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  maxLength={20}
                />
              </div>

              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={form.ativo}
                  onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                />
                <Label htmlFor="ativo">Ativo</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={criar.isPending || atualizar.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── AlertDialog Inativar ─────────────────────────────────────── */}
      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar condutor</AlertDialogTitle>
            <AlertDialogDescription>
              O condutor será inativado e não aparecerá nos selects de novos MDFes. Histórico de
              documentos já emitidos é preservado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (inativarId) {
                  excluir.mutate(inativarId);
                  setInativarId(null);
                }
              }}
            >
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
